import {
  Component, ChangeDetectionStrategy, Input, ViewChild, ElementRef, ChangeDetectorRef, Output, EventEmitter
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { HotelPromo } from '@shared/sliders/entities/slide-models';
import { TranslatePipe } from '@app/translate/TranslatePipe';
import { IMAGE_TYPE } from '@ux/components/image/constants/image-type.constants';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { priceToNightsLabel } from '@ux/mappers/guest.mapper';
import { TagType } from '@ux/components/member-rate-tag/entities/tag-type';
import { getHotelTagType } from '@ux/components/member-rate-tag/mappers/tag.mapper';
import { isNullOrUndefined } from '@app/util/util';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { BenefitViewType } from '@shared/benefits/entities/view-type';
import { SlideAbstractComponent } from '@shared/sliders/abstracts/slide.abstract';
import {
  TagMemberStyle, TagMemberSize, TagMemberType
} from '@design-system/components/molecules/tag-member/entities/tag-member.types';
import { TagProductType } from '@design-system/components/organisms/tag-product/entities/tag-product.types';
import { TagBrandingType } from '@design-system/components/organisms/tag-product/entities/tag-branding.types';
import { searchByDeeplink } from '@shared/sliders/helpers/search-by-deeplink.helper';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { FinancialPartnersInfoVM } from '@shared/benefits/entities/benefits';
import { TagHighlightType } from '@design-system/components/molecules/tag-highlight/entities/tag-highlight.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { ButtonIconSize, ButtonIconType } from '@design-system/components/molecules/button-icon/entities/button-icon.types';
import { HotelSlideWidth } from '@ux/components/slide/entities/slide.types';
import { ImageCdnPipe } from '@app/pipes/image-cdn.pipe';

const scroll = 20;
const speed = 0.6;

@Component({
  selector: 'app-slide-hotel-promo',
  templateUrl: './slide-hotel-promo.component.html',
  styleUrls: ['./slide-hotel-promo.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SlideHotelPromoComponent extends SlideAbstractComponent {
  @ViewChild('sectionSlideHotelPromo') content: ElementRef;
  @ViewChild('slideItem') slideItem: ElementRef;

  @Input() hotelPromos: HotelPromo[];
  @Input() index: number;
  @Input() isMobile: boolean;
  @Input() source: EVENT_SOURCE;
  @Input() salesChannel: string = null;
  @Input() hasRappiCard: boolean;
  @Input() isPrime: boolean;
  @Input() isLoggedIn: boolean;
  @Input() pageCode: string = null;

  @Output() selectPromo: EventEmitter<HotelPromo> = new EventEmitter<HotelPromo>();
  @Output() viewFinancialPartners = new EventEmitter<{
    partnerInfo: FinancialPartnersInfoVM,
    searchBy: SearchBy
  }>();

  public typeImageHotel: string = IMAGE_TYPE.HOTEL;
  public amenitiesBasePath: string = 'web/travel/amenities';
  public marriottProvider = HotelProvider.MarriottBonvoy;
  public benefitViewType = BenefitViewType.Cms;
  public tagProductType = TagProductType.Informative;
  public brandingTagProduct = TagBrandingType.Marriott;

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly tagMemberStyle = TagMemberStyle.Strikethrough;
  public readonly tagMemberSize = TagMemberSize.SM;

  public readonly tagHighlightType = TagHighlightType;

  public readonly buttonIconSize = ButtonIconSize.XL;
  public readonly buttonType = ButtonIconType.Secondary;

  public readonly slideWidth = HotelSlideWidth;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly translatePipe: TranslatePipe,
    protected router: Router,
    protected changeDetector: ChangeDetectorRef,
    protected imageCdnPipe: ImageCdnPipe,
  ) {
    super(router, changeDetector, imageCdnPipe)
  }

  get tagMemberType(): TagMemberType {
    return this.isPrime ? TagMemberType.Pro : TagMemberType.Pay;
  }

  get isMarriottPromo(): boolean {
    return this.source === 'marriottbonvoy' as EVENT_SOURCE;
  }

  public tagType(promo: HotelPromo): TagType {
    return getHotelTagType(
      promo.member_rate_indicator,
      null, this.hasRappiCard, this.isPrime, this.isLoggedIn, this.pageCode, this.route?.snapshot?.paramMap?.get('code')
    );
  }
  public priceToNights(nights: number): string {
    return priceToNightsLabel(nights);
  }

  public hasAmmenities(ammenities: number[]): boolean {
    return isNullOrUndefined(ammenities);
  }

  public hotelAmenities(ammenties: number[]): number[] {
    return ammenties.length > 5 ? ammenties.slice(0, 5) : ammenties;
  }

  public getStars(stars: number): number[] {
    return stars ? [].constructor(stars) : [];
  }

  public imageAmenity(name: number): string {
    return `${name}.svg`;
  }
  public selectHotelPromo(hotelPromo: HotelPromo): void {
    this.selectPromo.emit(hotelPromo);
    this.selectSlide(hotelPromo?.deeplink, hotelPromo?.deeplink_type);
  }

  public textDiscount(hotel: HotelPromo): string {
    if (hotel.discount_percent > 0) {
      return this.translatePipe.transform('HOTELS_MODULE.RESULT_HOTELS.OFF', {
        percentage: hotel.discount_percent,
      });
    }
  }

  public searchByDeeplink(deeplink: string): SearchBy | null {
    return searchByDeeplink(deeplink);
  }

  public toggleFinancialPartners(partnerInfo: FinancialPartnersInfoVM = null, searchBy: SearchBy = null): void {
    this.viewFinancialPartners.emit({ partnerInfo, searchBy });
  }
}
